import { Button } from '@app/components/various/Button';
import { Notification } from '@app/components/various/Notification';
import { getFailedOrderDetails, getOrderId } from '@app/ducks';
import { compiledPaths, paths } from '@app/paths';
import { useAmplitude } from '@app/utils/analytics/useAmplitude';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { report } from '@app/utils/monitoring';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { CancelledProductsFailure } from './CheckoutFailures/CancelledProductsFailure';
import { CreditLimitExceededFailure } from './CheckoutFailures/CreditLimitExceededFailure';
import { ExpiredDeliveryWindowsFailure } from './CheckoutFailures/ExpiredDeliveryWindowsFailure';
import { MinimumQuantityFailure } from './CheckoutFailures/MinimumQuantityFailure';
import { UnavailableProductsFailure } from './CheckoutFailures/UnavailableProductsFailure';
import { UnpaidInvoicesFailure } from './CheckoutFailures/UnpaidInvoicesFailure';
import { WrongMultipleQuantityFailure } from './CheckoutFailures/WrongMultipleQuantityFailure';

export const CheckoutFailure = () => {
  const { t } = useTranslation(['checkout']);
  const failedOrderDetails = useSelector(getFailedOrderDetails);
  const orderId = useSelector(getOrderId);
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();

  React.useLayoutEffect(() => {
    if (!isDefined(orderId)) {
      navigate(paths.ROOT, { replace: true });
    }
  }, [navigate, orderId]);

  const cancelledVariants = React.useMemo(() => {
    return failedOrderDetails?.cancelled ?? [];
  }, [failedOrderDetails?.cancelled]);

  const unavailableVariants = React.useMemo(() => {
    return (failedOrderDetails?.unavailable ?? []).filter(
      variant => !cancelledVariants.find(cancelled => cancelled.variantId === variant.variantId),
    );
  }, [cancelledVariants, failedOrderDetails?.unavailable]);

  const minimumQuantityVariants = React.useMemo(() => {
    return failedOrderDetails?.itemQuantityLessThanMinimum ?? [];
  }, [failedOrderDetails?.itemQuantityLessThanMinimum]);

  const wrongMultipleQuantityVariants = React.useMemo(() => {
    return failedOrderDetails?.itemQuantityWrongMultiple ?? [];
  }, [failedOrderDetails?.itemQuantityWrongMultiple]);

  const isCreditLimitExceeded = failedOrderDetails?.creditLimitExceeded ?? false;
  const hasUnpaidInvoices = failedOrderDetails?.hasUnpaidInvoices ?? false;

  const expiredDeliveryWindowsIds = React.useMemo(() => {
    if (Array.isArray(failedOrderDetails?.errors) || !isDefined(failedOrderDetails?.errors?.order?.expiredDeliveryWindowsIds)) {
      return [];
    }

    return failedOrderDetails.errors.order.expiredDeliveryWindowsIds;
  }, [failedOrderDetails?.errors]);

  const hasOrderFailedWithMessages = React.useMemo(() => {
    return isDefined(failedOrderDetails?.messages) && !isEmpty(failedOrderDetails.messages) && isDefined(failedOrderDetails.errors);
  }, [failedOrderDetails]);

  const products = React.useMemo(() => {
    return Object.values(failedOrderDetails?.products ?? {});
  }, [failedOrderDetails?.products]);

  React.useEffect(() => {
    const knownFailureConditions = [
      { condition: isCreditLimitExceeded, error: 'creditLimitExceeded' },
      { condition: hasUnpaidInvoices, error: 'hasUnpaidInvoices' },
      { condition: !isEmpty(expiredDeliveryWindowsIds), error: 'expiredDeliveryWindowsIds' },
      { condition: !isEmpty(unavailableVariants), error: 'unavailableVariants' },
      { condition: !isEmpty(cancelledVariants), error: 'cancelledVariants' },
      { condition: !isEmpty(minimumQuantityVariants), error: 'minimumQuantityVariants' },
      { condition: !isEmpty(wrongMultipleQuantityVariants), error: 'wrongMultipleQuantityVariants' },
      { condition: hasOrderFailedWithMessages, error: 'failedOrderDetails' },
    ];

    knownFailureConditions.forEach(({ condition, error }) => {
      if (condition) {
        trackEvent({ name: 'checkout.failure.reason', properties: { error } });
      }
    });

    const hasUnexepectedError = knownFailureConditions.every(({ condition }) => !condition);

    if (hasUnexepectedError) {
      report(`Checkout failed with unexpected error. See Additional Data section for more details`, {
        checkoutError: failedOrderDetails?.errors,
      });
      trackEvent({ name: 'checkout.failure.reason', properties: { error: 'unexpectedError' } });
    }

    trackEvent({ name: 'checkout.failure' });
  }, [
    trackEvent,
    isCreditLimitExceeded,
    hasUnpaidInvoices,
    expiredDeliveryWindowsIds,
    unavailableVariants,
    cancelledVariants,
    minimumQuantityVariants,
    wrongMultipleQuantityVariants,
    hasOrderFailedWithMessages,
    failedOrderDetails?.errors,
  ]);

  return (
    <div className="mx-auto flex w-full max-w-screen-xl flex-col items-start gap-5 px-4 py-4 lg:px-8 lg:py-8">
      <h2 className="heading-2">{t('checkout:checkout_failed')}</h2>
      <div className="w-full [&>*+*]:mt-5 [&>*+*]:border-t [&>*+*]:border-t-neutral-90 [&>*+*]:pt-5">
        {failedOrderDetails?.messages?.map((message, index) => (
          <div key={index}>
            <Notification type="error" message={message} />
          </div>
        ))}
        {isCreditLimitExceeded && <CreditLimitExceededFailure />}
        {hasUnpaidInvoices && <UnpaidInvoicesFailure />}
        {!isEmpty(expiredDeliveryWindowsIds) && <ExpiredDeliveryWindowsFailure deliveryWindowIds={expiredDeliveryWindowsIds} />}
        {!isEmpty(unavailableVariants) && <UnavailableProductsFailure variants={unavailableVariants} products={products} />}
        {!isEmpty(cancelledVariants) && <CancelledProductsFailure variants={cancelledVariants} products={products} />}
        {!isEmpty(minimumQuantityVariants) && <MinimumQuantityFailure variants={minimumQuantityVariants} products={products} />}
        {!isEmpty(wrongMultipleQuantityVariants) && (
          <WrongMultipleQuantityFailure variants={wrongMultipleQuantityVariants} products={products} />
        )}
      </div>
      {isDefined(orderId) &&
        (isCreditLimitExceeded || hasUnpaidInvoices ?
          <Button
            as={Link}
            to={compiledPaths.CHECKOUT_ORDER({ id: orderId })}
            className="mx-auto w-full max-w-screen-xxs sm:mx-0 sm:w-auto"
          >
            {t('checkout:back_to_checkout')}
          </Button>
        : <Button as={Link} to={compiledPaths.ORDER_DETAILS({ id: orderId })} className="mx-auto w-full max-w-screen-xxs sm:mx-0 sm:w-auto">
            {t('checkout:back_to_my_selection')}
          </Button>)}
    </div>
  );
};
